import * as z from "zod";

import { useAdminAuthStore } from "@/features/admin/stores/useAdminAuthStore";

// Global fetch wrapper for 401 handling
async function adminFetch(
  url: string,
  options?: RequestInit,
): Promise<Response> {
  const response = await fetch(url, options);

  if (response.status === 401) {
    useAdminAuthStore.getState().logoutUser();
    throw new Error("Authentication required");
  }

  return response;
}

// Auth Schemas
const AuthUserSchema = z.object({
  id: z.string(),
  username: z.string(),
});

const LoginResponseSchema = z.object({
  success: z.boolean(),
  user: AuthUserSchema.optional(),
  error: z.string().optional(),
});

const AuthCheckResponseSchema = z.object({
  authenticated: z.boolean(),
  user: AuthUserSchema.nullable(),
});

const LogoutResponseSchema = z.object({
  success: z.boolean(),
});

// Auth API Functions
export async function login(data: {
  username: string;
  password: string;
  rememberMe?: boolean;
}) {
  const response = await fetch("/api/admin-login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Login request failed");
  }

  return LoginResponseSchema.parse(await response.json());
}

export async function logout() {
  const response = await fetch("/api/admin-logout", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    throw new Error("Logout request failed");
  }

  return LogoutResponseSchema.parse(await response.json());
}

export async function checkAuth() {
  const response = await fetch("/api/admin-auth-check");

  if (!response.ok) {
    throw new Error("Auth check request failed");
  }

  return AuthCheckResponseSchema.parse(await response.json());
}

// Event Management
const EventSchema = z.object({
  event_id: z.number(),
  name: z.string(),
  submission_count: z.number(),
  created_at: z.number(),
  updated_at: z.number(),
});

export type EventData = z.infer<typeof EventSchema>;

const EventsResponseSchema = z.object({
  events: z.array(EventSchema),
});

const CreateEventResponseSchema = z.object({
  success: z.boolean(),
  event: EventSchema.optional(),
  archived_count: z.number().optional(),
  message: z.string(),
});

export async function fetchEvents() {
  const response = await adminFetch("/api/admin-events");

  if (!response.ok) {
    throw new Error("Failed to fetch events");
  }

  return EventsResponseSchema.parse(await response.json());
}

export async function createEvent(name: string) {
  const response = await adminFetch("/api/admin-create-event", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    throw new Error("Failed to create event");
  }

  return CreateEventResponseSchema.parse(await response.json());
}

// Admin Event Data Schemas (empower-specific)
const EventSubmissionDataSchema = z.object({
  submission_id: z.number(),
  email_address: z.string().nullable(),
  full_name: z.string(),
  phone_number: z.string().nullable(),
  group_size: z.number(),
  prompt_1_answer: z.string().nullable(),
  prompt_2_answer: z.string().nullable(),
  driver_profile: z.string().nullable(),
  // raw PDO/asArray() results serialize tinyint columns as 0/1, not JSON booleans
  deliver_via_email: z.number(),
  deliver_via_sms: z.number(),
  submitted_photo_path: z.string(),
  submitted_photo_url: z.string().nullable(),
  created_at: z.number(),
  updated_at: z.number(),
});

const EventCompositeDataSchema = z.object({
  composite_id: z.number(),
  uuid: z.string().nullable(),
  queue_job_id: z.string().nullable(),
  submission_id: z.number(),
  composite_path: z.string().nullable(),
  composite_url: z.string().nullable(),
  is_hidden: z.boolean(),
  created_at: z.number(),
  updated_at: z.number(),
});

const EventQueueJobDataSchema = z.object({
  queue_job_id: z.string(),
  queue_id: z.number().nullable(),
  submission_id: z.number().nullable(),
  className: z.string(),
  status: z.string(),
  input: z.any(),
  output: z.any().nullable(),
  error: z.string().nullable(),
  created_at: z.number(),
  updated_at: z.number(),
});

const EventPrintQueueDataSchema = z.object({
  print_queue_id: z.number(),
  composite_id: z.number(),
  image_path: z.string().nullable(),
  image_url: z.string().nullable(),
  copies: z.number(),
  status: z.string(),
  claimed_by: z.string().nullable(),
  claimed_at: z.number().nullable(),
  error_message: z.string().nullable(),
  created_at: z.number(),
  updated_at: z.number(),
});

const DataArchiveSchema = z.object({
  data_archive_id: z.number(),
  event_id: z.number().nullable(),
  include_photos: z.boolean(),
  status: z.enum(["queued", "processing", "completed", "failed"]),
  file_size: z.number().nullable(),
  download_url: z.string().nullable(),
  error: z.string().nullable(),
  queue_job_id: z.string().nullable(),
  requested_by_user_id: z.number().nullable(),
  created_at: z.number(),
  updated_at: z.number(),
  completed_at: z.number().nullable(),
});

const AdminEventDataResponseSchema = z.object({
  submissions: z.array(EventSubmissionDataSchema),
  composites: z.array(EventCompositeDataSchema),
  queue_jobs: z.array(EventQueueJobDataSchema),
  data_archives: z.array(DataArchiveSchema),
  print_queue: z.array(EventPrintQueueDataSchema),
  metadata: z.object({
    event_id: z.number().nullable(),
    is_current_event: z.boolean(),
    timestamp: z.number(),
  }),
});

export type EventSubmissionData = z.infer<typeof EventSubmissionDataSchema>;
export type EventCompositeData = z.infer<typeof EventCompositeDataSchema>;
export type EventQueueJobData = z.infer<typeof EventQueueJobDataSchema>;
export type EventPrintQueueData = z.infer<typeof EventPrintQueueDataSchema>;
export type DataArchive = z.infer<typeof DataArchiveSchema>;
export type AdminEventDataResponse = z.infer<
  typeof AdminEventDataResponseSchema
>;

export async function fetchAdminEventData(
  changesSince?: number,
  eventId?: number | null,
) {
  const searchParams = new URLSearchParams();

  if (changesSince !== undefined) {
    searchParams.append("changes_since", changesSince.toString());
  }

  if (eventId !== undefined && eventId !== null) {
    searchParams.append("event_id", eventId.toString());
  }

  const url = `/api/admin-event-data?${searchParams}`;
  const response = await adminFetch(url);

  if (!response.ok) {
    throw new Error("Failed to fetch admin event data");
  }

  return AdminEventDataResponseSchema.parse(await response.json());
}

// Generate Composite
export async function generateComposite(data: { submission_id: number }) {
  const response = await adminFetch("/api/admin-generate-composite", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to generate composite");
  }

  return z
    .object({
      success: z.boolean(),
      composite_id: z.number(),
      compose_job_id: z.string(),
      message: z.string(),
    })
    .parse(await response.json());
}

export async function regenerateDriverProfile(userId: number) {
  const response = await adminFetch("/api/admin-regenerate-driver-profile", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ submission_id: userId }),
  });

  if (!response.ok) {
    throw new Error("Failed to regenerate driver profile");
  }

  return z
    .object({
      success: z.boolean(),
      queue_job_id: z.string(),
      submission_id: z.number(),
      message: z.string(),
    })
    .parse(await response.json());
}

export async function fetchFollowUpPreviewHtml(uuid: string) {
  const response = await adminFetch(
    `/api/admin-follow-up-preview?uuid=${encodeURIComponent(uuid)}`,
  );

  if (!response.ok) {
    throw new Error("Failed to load follow-up preview");
  }

  return response.text();
}

const CompositePromptSchema = z.object({
  success: z.boolean(),
  composite_id: z.number(),
  group_size: z.number(),
  config_key: z.string(),
  orientation: z.string(),
  prompt: z.string().nullable(),
  reference_images: z.array(
    z.object({
      label: z.string().nullable(),
      filename: z.string(),
    }),
  ),
  current_provider: z.object({
    provider: z.string(),
    model: z.string().nullable(),
  }),
  request_settings: z.object({
    endpoint: z.string(),
    size: z.string(),
    quality: z.string(),
    n: z.number(),
  }),
});

export type CompositePrompt = z.infer<typeof CompositePromptSchema>;

export async function fetchCompositePrompt(compositeId: number) {
  const response = await adminFetch(
    `/api/admin-composite-prompt?composite_id=${compositeId}`,
  );

  if (!response.ok) {
    throw new Error("Failed to load AI prompt");
  }

  return CompositePromptSchema.parse(await response.json());
}

// Upload User Image
export async function uploadUserImage(data: {
  submission_id: number;
  file: File;
}) {
  const formData = new FormData();
  formData.append("submission_id", data.submission_id.toString());
  formData.append("photo", data.file);

  const response = await adminFetch("/api/admin-upload-user-image", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to upload user image");
  }

  return z
    .object({
      success: z.boolean(),
      photo_url: z.string(),
      message: z.string(),
    })
    .parse(await response.json());
}

// Manual Update Photo
export async function manualUpdatePhoto(data: {
  submission_id: number;
  composite_id: number;
  photo: File;
}) {
  const formData = new FormData();
  formData.append("submission_id", data.submission_id.toString());
  formData.append("composite_id", data.composite_id.toString());
  formData.append("photo", data.photo);

  const response = await adminFetch("/api/admin-manual-update-photo", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to manually update photo");
  }

  return z
    .object({
      success: z.boolean(),
      path: z.string().optional(),
    })
    .parse(await response.json());
}

export async function toggleCompositeHidden(
  compositeId: number,
  isHidden: boolean,
) {
  const response = await adminFetch("/api/admin-toggle-composite-hidden", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      composite_id: compositeId,
      is_hidden: isHidden,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to update composite visibility");
  }

  return z
    .object({
      success: z.boolean(),
      composite: z.object({
        composite_id: z.number(),
        is_hidden: z.boolean(),
      }),
      message: z.string(),
    })
    .parse(await response.json());
}

// Queue Jobs
const QueueJobSchema = z.object({
  queue_job_id: z.string(),
  queue_id: z.number().nullable(),
  className: z.string().nullable(),
  status: z.string(),
  input: z.any(),
  output: z.any().nullable(),
  error: z.string().nullable(),
});

// Print Queue
export async function retryPrintJob(printQueueId: number) {
  const response = await adminFetch("/api/admin-retry-print-job", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ print_queue_id: printQueueId }),
  });

  if (!response.ok) {
    throw new Error("Failed to retry print job");
  }

  return z
    .object({
      success: z.boolean(),
      print_queue_id: z.number(),
      message: z.string(),
    })
    .parse(await response.json());
}

export async function cancelPrintJobs(printQueueIds?: number[]) {
  const response = await adminFetch("/api/admin-cancel-print-jobs", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    // Omitting the ids (undefined, or an empty array - the backend's
    // empty() check treats both the same) cancels every pending job.
    body: JSON.stringify(
      printQueueIds?.length ? { print_queue_ids: printQueueIds } : {},
    ),
  });

  if (!response.ok) {
    throw new Error("Failed to cancel print jobs");
  }

  return z
    .object({
      success: z.boolean(),
      cancelled_count: z.number(),
    })
    .parse(await response.json());
}

const PrintClientLogSchema = z.object({
  print_client_log_id: z.number(),
  client_name: z.string(),
  client_timestamp: z.number().nullable(),
  severity: z.string(),
  message: z.string(),
  created_at: z.number(),
});

export type PrintClientLog = z.infer<typeof PrintClientLogSchema>;

export async function getPrintClientLogs(filters: {
  clientName?: string;
  severity?: string;
  search?: string;
  limit?: number;
}) {
  const searchParams = new URLSearchParams();
  if (filters.clientName)
    searchParams.append("client_name", filters.clientName);
  if (filters.severity) searchParams.append("severity", filters.severity);
  if (filters.search) searchParams.append("search", filters.search);
  searchParams.append("limit", (filters.limit ?? 1000).toString());

  const response = await adminFetch(
    `/api/admin-print-client-log?${searchParams}`,
  );

  if (!response.ok) {
    throw new Error("Failed to fetch print client logs");
  }

  return z
    .object({
      logs: z.array(PrintClientLogSchema),
      clients: z.array(z.string()),
    })
    .parse(await response.json());
}

export async function fetchQueueJobs() {
  const response = await adminFetch("/api/admin-queue-jobs");
  if (!response.ok) {
    throw new Error("Failed to fetch queue jobs");
  }

  return z
    .object({
      success: z.boolean(),
      jobs: z.array(QueueJobSchema),
      total: z.number(),
    })
    .parse(await response.json());
}

// Retry Queue Job
export async function retryQueueJob(queueJobId: string) {
  const response = await adminFetch("/api/admin-retry-queue-job", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ queue_job_id: queueJobId }),
  });

  if (!response.ok) {
    throw new Error("Failed to retry queue job");
  }

  return z
    .object({
      success: z.boolean(),
      old_queue_job_id: z.string(),
      new_queue_job_id: z.string(),
      message: z.string(),
    })
    .parse(await response.json());
}

// Bulk Retry Queue Jobs
export async function bulkRetryQueueJobs(queueJobIds: string[]) {
  const response = await adminFetch("/api/admin-bulk-retry-queue-jobs", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ queue_job_ids: queueJobIds }),
  });

  if (!response.ok) {
    throw new Error("Failed to bulk retry queue jobs");
  }

  return z
    .object({
      success: z.boolean(),
      retried_count: z.number(),
      failed_count: z.number(),
      results: z.array(
        z.object({
          old_queue_job_id: z.string(),
          new_queue_job_id: z.string(),
          success: z.boolean(),
        }),
      ),
      failures: z.array(
        z.object({
          queue_job_id: z.string(),
          error: z.string(),
        }),
      ),
      message: z.string(),
    })
    .parse(await response.json());
}

// Test Gemini
export type AiModel = "gemini" | "openai" | "chatgpt-image-latest";

export async function testGemini(data: {
  image: File;
  prompt: string;
  model?: AiModel;
}) {
  const formData = new FormData();
  formData.append("image", data.image);
  formData.append("prompt", data.prompt);
  if (data.model) {
    formData.append("model", data.model);
  }

  const response = await adminFetch("/api/admin-test-gemini", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to test AI model: ${errorText}`);
  }

  return z
    .object({
      success: z.boolean(),
      output_image: z.string(),
      message: z.string(),
      model: z.string().optional(),
    })
    .parse(await response.json());
}

// Config Schemas
const ConfigSchema = z.object({
  key: z.string(),
  value: z.string().nullable(),
  schema: z.string().nullable(),
});

const ConfigListResponseSchema = z.object({
  success: z.boolean(),
  configs: z.array(ConfigSchema),
});

const ConfigGetResponseSchema = z.object({
  success: z.boolean(),
  config: ConfigSchema.optional(),
  error: z.string().optional(),
});

const ConfigUpdateResponseSchema = z.object({
  success: z.boolean(),
  config: ConfigSchema.optional(),
  error: z.string().optional(),
});

export type Config = z.infer<typeof ConfigSchema>;

export async function fetchConfigs() {
  const response = await adminFetch("/api/admin-config");

  if (!response.ok) {
    throw new Error("Failed to fetch configs");
  }

  return ConfigListResponseSchema.parse(await response.json());
}

export async function fetchConfig(key: string) {
  const response = await adminFetch(
    `/api/admin-config?key=${encodeURIComponent(key)}`,
  );

  if (!response.ok) {
    throw new Error("Failed to fetch config");
  }

  return ConfigGetResponseSchema.parse(await response.json());
}

export async function updateConfig(
  key: string,
  updates: { value?: string | null; schema?: string | null },
) {
  const response = await adminFetch("/api/admin-config", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ key, ...updates }),
  });

  if (!response.ok) {
    throw new Error("Failed to update config");
  }

  return ConfigUpdateResponseSchema.parse(await response.json());
}

// Stats Reports
const StatsMetricSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
});

const StatsMetricsResponseSchema = z.object({
  metrics: z.array(StatsMetricSchema),
});

export type StatsMetric = z.infer<typeof StatsMetricSchema>;

const StatsReportMetricDataSchema = z.object({
  name: z.string(),
  description: z.string(),
  data: z.array(z.record(z.string(), z.any())),
});

const StatsReportResponseSchema = z.object({
  generated_at: z.string(),
  metrics: z.record(z.string(), StatsReportMetricDataSchema),
});

export type StatsReportMetricData = z.infer<typeof StatsReportMetricDataSchema>;
export type StatsReportResponse = z.infer<typeof StatsReportResponseSchema>;

export async function fetchStatsMetrics() {
  const response = await adminFetch("/api/admin-stats-metrics");

  if (!response.ok) {
    throw new Error("Failed to fetch stats metrics");
  }

  return StatsMetricsResponseSchema.parse(await response.json());
}

export async function fetchStatsReport(metricKeys?: string[]) {
  const params = new URLSearchParams({ format: "json" });
  if (metricKeys && metricKeys.length > 0) {
    params.append("metrics", metricKeys.join(","));
  }

  const response = await adminFetch(`/api/admin-stats-report?${params}`);

  if (!response.ok) {
    throw new Error("Failed to fetch stats report");
  }

  return StatsReportResponseSchema.parse(await response.json());
}

export async function downloadStatsReportCsv(
  metricKeys?: string[],
  filename?: string,
) {
  const params = new URLSearchParams({ format: "csv" });
  if (metricKeys && metricKeys.length > 0) {
    params.append("metrics", metricKeys.join(","));
  }
  if (filename) {
    params.append("filename", filename);
  }

  // Fetch the CSV data
  const response = await adminFetch(`/api/admin-stats-report?${params}`);

  if (!response.ok) {
    throw new Error("Failed to download CSV");
  }

  // Get the blob data
  const blob = await response.blob();

  // Create a temporary anchor element to trigger download
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "stats-report.csv";
  document.body.appendChild(a);
  a.click();

  // Cleanup
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// Data Archives (event data dump)
export async function queueDataArchive(data: {
  event_id?: number | null;
  include_photos: boolean;
}) {
  const response = await adminFetch("/api/admin-data-dump", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      event_id: data.event_id ?? null,
      include_photos: data.include_photos,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to queue data archive");
  }

  return z
    .object({
      success: z.boolean(),
      data_archive_id: z.number(),
      queue_job_id: z.string(),
    })
    .parse(await response.json());
}

export function getDataArchiveDownloadUrl(archive: DataArchive) {
  return archive.download_url ?? "";
}

export async function deleteDataArchive(dataArchiveId: number) {
  const response = await adminFetch("/api/admin-delete-data-archive", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ data_archive_id: dataArchiveId }),
  });

  if (!response.ok) {
    throw new Error("Failed to delete data archive");
  }

  return z
    .object({
      success: z.boolean(),
      data_archive_id: z.number(),
    })
    .parse(await response.json());
}
